import {
	Highlight,
	HighlightedText,
	IconAndText,
	Space,
	PipeSeparator
} from "client/Components";
import { useHighlights } from "client/Features/Layout/TitleBar/GlobalSearch/ResultView/CategoryView/ItemView/useHighlights";
import { EventGlobalSearchResultItem, GlobalSearchScopeIdentifier } from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { ReactElement } from "react";
import { ItemLayout } from "./ItemLayout";
import {
	generateTrackingId,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { NumberFormatter, useKeyboardListNavigationItem } from "client/Hooks";
import { useHubStatisticsFormatter } from "client/Features/Hub/useHubStatisticsFormatter";
import { hubItemKindToIcon, HubThumbnail } from "client/Features/Hub/HubThumbnail/HubThumbnail";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { formatDate } from "client_legacy/ndaw/dateFormatting";
import { EventLocationType } from "client/Api/Hub/Events/eventViewer";
import { localize } from "client_legacy/ndaw/culture";
import { TimeZone } from "client/DateAndTime/timezones";
import { useEventNavigationHandler } from "client/Features/Layout/TitleBar/GlobalSearch/Navigation/useEventNavigationHandler";
import { SearchListItem } from "../SearchListItem";
import { Key } from "client/Misc/commonTypes";

export type EventItemViewProps = {
	item: EventGlobalSearchResultItem;
	navigationIndex: number;
	itemScope: GlobalSearchScopeIdentifier;
	isMobile: boolean;
	timezone: TimeZone;
};

export const EventItemView = (props: EventItemViewProps): ReactElement => {
	const {
		item,
		navigationIndex,
		itemScope,
		isMobile,
		timezone
	} = props;

	const id: number = item.event.id;

	const executeHandler: () => void = useEventNavigationHandler({
		id,
		searchResult: item,
		itemScope
	});

	return (
		<SearchListItem
			onExecute={executeHandler}
			trackingId={generateTrackingId("event-item", `|${id}`)}
		>
			<EventItem
				item={item}
				isMobile={isMobile}
				timezone={timezone}
				navigationIndex={navigationIndex}
				onExecute={executeHandler}
			/>
		</SearchListItem>
	);
};
EventItemView.displayName = "EventItemView";

type EventItemProps = {
	item: EventGlobalSearchResultItem;
	isMobile: boolean;
	timezone: TimeZone;
	navigationIndex: number;
	onExecute: () => void;
};

const EventItem = (props: EventItemProps): ReactElement => {
	const {
		item,
		isMobile,
		timezone,
		navigationIndex,
		onExecute
	} = props;

	const {
		hitHighlights,
		event
	} = item;

	const {
		title,
		media,
		attendeeCount,
		author,
		category,
		descriptionPreview,
		startDate,
		locationType,
		location
	} = event;

	const highlights: Map<string, Highlight> = useHighlights(hitHighlights);

	const statisticsFormatter: NumberFormatter = useHubStatisticsFormatter();

	const shouldShowOnlineLocation: boolean = locationType === EventLocationType.Hybrid
		|| locationType === EventLocationType.Online;

	const shouldShowPhysicalLocation: boolean = locationType === EventLocationType.Hybrid
		|| locationType === EventLocationType.Physical;

	const { ref, onKeyDown } = useKeyboardListNavigationItem({
		itemIndex: navigationIndex,
		onExecute: onExecute,
		executeKey: [Key.Enter, Key.Space],
	});

	return (
		<ItemLayout trackingId="event-info"
			ref={ref}
			onKeyDown={onKeyDown}
			tabIndex={-1}
		>
			<ItemLayout.IconContainer>
				<ItemLayout.FontIcon
					icon={hubItemKindToIcon[HubItemKind.Event]}
				/>
			</ItemLayout.IconContainer>
			<ItemLayout.InfoWrapper>
				<ItemLayout.Title trackingId="title">
					<HighlightedText
						text={title}
						highlight={highlights.get("event.title")}
					/>
				</ItemLayout.Title>
				<ItemLayout.Info>
					{isNotNil(author) && (
						<>
							{localize("_(Edited)By")}
							<Space />
							<span
								{...setTrackingId("author")}
							>
								{author.name}
							</span>
						</>
					)}
					{isNotNil(category) && (
						<>
							<PipeSeparator />
							<span
								{...setTrackingId("category")}
							>
								{category.name}
							</span>
						</>
					)}
				</ItemLayout.Info>
				<ItemLayout.Content>
					<HighlightedText
						text={descriptionPreview}
						highlight={highlights.get("event.descriptionPreview")}
					/>
				</ItemLayout.Content>
				<ItemLayout.Statistics trackingId="statistics">
					<IconAndText
						text={
							formatDate(
								startDate,
								"DMYT",
								timezone
							)
						}
					>
						<IconAndText.Icon
							value="calendar"
							size="small"
						/>
					</IconAndText>
					{shouldShowPhysicalLocation && location && (
						<IconAndText
							text={location}
						>
							<IconAndText.Icon
								value="location-dot"
								size="small"
							/>
						</IconAndText>
					)}
					{shouldShowOnlineLocation && (
						<IconAndText
							text={localize("_Online")}
						>
							<IconAndText.Icon
								size="small"
								value="video"
							/>
						</IconAndText>
					)}

					{isNotNil(attendeeCount) && (
						<IconAndText
							text={statisticsFormatter(attendeeCount)}
						>
							<IconAndText.Icon
								value="user-group"
								size="small"
							/>
						</IconAndText>
					)}
				</ItemLayout.Statistics>
			</ItemLayout.InfoWrapper>
			{!isMobile && (
				<ItemLayout.Thumbnail>
					<HubThumbnail
						hubItemKind={HubItemKind.Event}
						media={media}
						title={title}
						mediaPlaceholderSize="medium"
					/>
				</ItemLayout.Thumbnail>
			)}
		</ItemLayout>
	);
};
EventItem.displayName = "EventItem";
